import * as React from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const SEARCH_THRESHOLD = 5;

const SearchableSelect = React.forwardRef(({
  value,
  onValueChange,
  options = [],
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results found.",
  disabled = false,
  className,
  testId,
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);
  // cmdk lowercases item values internally, so key the label map accordingly
  const labels = React.useMemo(
    () => new Map(options.map((o) => [String(o.value).toLowerCase(), o.label])),
    [options]
  );
  const filter = React.useCallback(
    (itemValue, search) =>
      (labels.get(String(itemValue).toLowerCase()) || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ? 1 : 0,
    [labels]
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={ref}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("line-clamp-1 text-left", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[8rem] p-0" align="start">
        <Command filter={filter}>
          {options.length > SEARCH_THRESHOLD && (
            <CommandInput placeholder={searchPlaceholder} className="h-9" />
          )}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt.value}
                value={String(opt.value)}
                onSelect={() => {
                  onValueChange(opt.value);
                  setOpen(false);
                }}
              >
                <Check className={cn("h-4 w-4", opt.value === value ? "opacity-100" : "opacity-0")} />
                {opt.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
SearchableSelect.displayName = "SearchableSelect";

export { SearchableSelect };
